import { useState } from "react";

interface LogoProps {
  className?: string;
  variant?: "full" | "horizontal" | "icon" | "badge";
  theme?: "light" | "dark";
  animate?: boolean;
}

const Logo = ({ className = "", variant = "horizontal", animate = true }: LogoProps) => {
  const [isHovered, setIsHovered] = useState(false);

  // Shared SVG gradient definitions
  const GradientDefs = () => (
    <defs>
      {/* Neon purple to pink gradient for fuel fill */}
      <linearGradient id="fuelGradient" x1="0%" y1="100%" x2="0%" y2="0%">
        <stop offset="0%" stopColor="hsl(330, 81%, 60%)" />
        <stop offset="100%" stopColor="hsl(271, 91%, 65%)" />
      </linearGradient>
      
      {/* Cyan glow gradient for outline */}
      <linearGradient id="outlineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="hsl(189, 94%, 43%)" />
        <stop offset="50%" stopColor="hsl(189, 94%, 53%)" />
        <stop offset="100%" stopColor="hsl(189, 94%, 43%)" />
      </linearGradient>
      
      {/* Text gradient purple to pink */}
      <linearGradient id="textGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="hsl(271, 91%, 65%)" />
        <stop offset="100%" stopColor="hsl(330, 81%, 60%)" />
      </linearGradient>
      
      {/* Shimmer effect */}
      <linearGradient id="shimmerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="transparent" />
        <stop offset="45%" stopColor="hsl(0, 0%, 100%)" stopOpacity="0.3" />
        <stop offset="55%" stopColor="hsl(0, 0%, 100%)" stopOpacity="0.3" />
        <stop offset="100%" stopColor="transparent" />
      </linearGradient>
      
      {/* Glow filter */}
      <filter id="glowFilter" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      
      {/* Stronger glow for hover */}
      <filter id="glowFilterStrong" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="4" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );

  // Battery/Canister SVG component
  const BatteryIcon = ({ size = 40 }: { size?: number }) => {
    const scale = size / 40;
    
    return (
      <svg 
        width={size} 
        height={size * 1.4} 
        viewBox="0 0 40 56" 
        fill="none"
        className={`transition-all duration-300 ${isHovered ? 'drop-shadow-[0_0_12px_hsl(189,94%,43%)]' : ''}`}
      >
        <GradientDefs />
        
        {/* Battery body outline with glow */}
        <rect 
          x="4" 
          y="8" 
          width="32" 
          height="44" 
          rx="4" 
          fill="transparent"
          stroke="url(#outlineGradient)" 
          strokeWidth="2"
          filter={isHovered ? "url(#glowFilterStrong)" : "url(#glowFilter)"}
          className={animate ? "animate-[logo-outline-pulse_3s_ease-in-out_infinite]" : ""}
        />
        
        {/* Battery cap */}
        <rect 
          x="14" 
          y="2" 
          width="12" 
          height="6" 
          rx="2" 
          fill="url(#outlineGradient)"
          className={animate ? "animate-[logo-outline-pulse_3s_ease-in-out_infinite]" : ""}
        />
        
        {/* Fuel fill level with animation */}
        <rect 
          x="7" 
          y="20" 
          width="26" 
          height="29" 
          rx="2" 
          fill="url(#fuelGradient)"
          className={animate ? "animate-[logo-fuel-breathe_2.5s_ease-in-out_infinite]" : ""}
        />
        
        {/* Glass highlight on fuel */}
        <rect 
          x="9" 
          y="22" 
          width="8" 
          height="20" 
          rx="1" 
          fill="hsl(0, 0%, 100%)"
          fillOpacity="0.15"
        />
        
        {/* Shimmer overlay */}
        {animate && (
          <rect 
            x="7" 
            y="20" 
            width="26" 
            height="29" 
            rx="2" 
            fill="url(#shimmerGradient)"
            className="animate-[logo-shimmer_2s_ease-in-out_infinite]"
            style={{ mixBlendMode: 'overlay' }}
          />
        )}
        
        {/* Play button triangle */}
        <polygon 
          points="16,30 16,42 26,36" 
          fill="white"
          filter="url(#glowFilter)"
          className={animate ? "animate-[logo-play-pulse_2s_ease-in-out_infinite]" : ""}
        />
        
        {/* "FULL" text indicator */}
        <text 
          x="20" 
          y="16" 
          textAnchor="middle" 
          fontSize="6" 
          fontWeight="bold" 
          fill="hsl(142, 71%, 45%)"
          className={animate ? "animate-[logo-text-glow_2s_ease-in-out_infinite]" : ""}
        >
          FULL
        </text>
        
        {/* Sparkle effects */}
        {animate && (
          <>
            <circle cx="36" cy="12" r="1.5" fill="white" className="animate-[twinkle_1.5s_ease-in-out_infinite]" />
            <circle cx="6" cy="46" r="1" fill="hsl(189, 94%, 43%)" className="animate-[twinkle_1.8s_ease-in-out_infinite_0.3s]" />
            <circle cx="38" cy="40" r="1" fill="hsl(330, 81%, 60%)" className="animate-[twinkle_2s_ease-in-out_infinite_0.6s]" />
          </>
        )}
      </svg>
    );
  };

  // "72" text component
  const NumberText = ({ size = 24 }: { size?: number }) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 50 32" fill="none">
      <GradientDefs />
      <text 
        x="25" 
        y="26" 
        textAnchor="middle" 
        fontSize="28" 
        fontWeight="900" 
        fontFamily="Montserrat, sans-serif"
        fill="url(#textGradient)"
        filter={isHovered ? "url(#glowFilterStrong)" : "url(#glowFilter)"}
        className={animate ? "animate-[logo-text-glow_3s_ease-in-out_infinite]" : ""}
      >
        72
      </text>
    </svg>
  );

  // Full variant - larger logo for hero sections
  if (variant === "full") {
    return (
      <div 
        className={`flex flex-col items-center gap-2 transition-transform duration-300 ${isHovered ? 'scale-105' : ''} ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative">
          <BatteryIcon size={64} />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-neon-cyan tracking-wider">FUEL</span>
          <NumberText size={28} />
        </div>
      </div>
    );
  }

  // Horizontal variant - for navbar/footer
  if (variant === "horizontal") {
    return (
      <div 
        className={`flex items-center gap-2 transition-transform duration-300 ${isHovered ? 'scale-105' : ''} ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <BatteryIcon size={32} />
        <div className="flex items-baseline gap-0.5">
          <span className="text-[10px] font-bold text-neon-cyan tracking-widest">FUEL</span>
          <NumberText size={18} />
        </div>
      </div>
    );
  }

  // Icon variant - compact for favicon-style uses
  if (variant === "icon") {
    return (
      <div 
        className={`flex items-center justify-center transition-transform duration-300 ${isHovered ? 'scale-110' : ''} ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <BatteryIcon size={28} />
      </div>
    );
  }

  // Badge variant - for social/circular contexts
  if (variant === "badge") {
    return (
      <div 
        className={`relative flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm p-3 border border-neon-cyan/30 transition-all duration-300 ${isHovered ? 'scale-105 border-neon-cyan/60 shadow-[0_0_20px_hsl(189,94%,43%,0.3)]' : ''} ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col items-center gap-1">
          <BatteryIcon size={36} />
          <NumberText size={14} />
        </div>
      </div>
    );
  }

  return null;
};

export default Logo;
